import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  doc, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  getDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db, storage, functions } from '../firebase/config';
import { deleteUser } from 'firebase/auth';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import Button from '../components/Button';
import Input from '../components/Input';
import { useAuth } from '../contexts/AuthContext';
import DeleteAccountModal from '../components/DeleteAccountModal';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl};
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  h2 {
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const UserCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: 4px;
`;

const UserInfo = styled.div`
  h3 {
    margin: 0;
    font-size: 1rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const DangerZone = styled(Section)`
  border: 1px solid ${({ theme }) => theme.colors.error};
  
  h2 {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const DeleteAccountButton = styled(Button)`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.error};
  border: 2px solid ${({ theme }) => theme.colors.error};

  &:hover {
    background: ${({ theme }) => theme.colors.error}10;
  }
`;

const Settings = () => {
  const { user, isPremium, logout } = useAuth();
  const [household, setHousehold] = useState(null);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchHousehold = async () => {
      try {
        if (!user) return;
        
        // Check if user is already in a household
        const householdsQuery = query(
          collection(db, 'households'),
          where('members', 'array-contains', user.uid)
        );
        const householdsSnapshot = await getDocs(householdsQuery);
        
        if (!householdsSnapshot.empty) {
          const householdDoc = householdsSnapshot.docs[0];
          const householdData = { id: householdDoc.id, ...householdDoc.data() };
          setHousehold(householdData);

          // Fetch shared users' details
          const sharedUsersData = await Promise.all(
            householdData.members
              .filter(memberId => memberId !== user.uid)
              .map(async (memberId) => {
                const userDoc = await getDoc(doc(db, 'users', memberId));
                return { id: userDoc.id, ...userDoc.data() };
              })
          );
          setSharedUsers(sharedUsersData);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching household:', error);
        setError('Failed to load household data');
        setLoading(false);
      }
    };

    fetchHousehold();
  }, [user]);

  const handleManageSubscription = async () => {
    try {
      const createPortalSession = httpsCallable(functions, 'createCustomerPortal');
      const { data } = await createPortalSession({
        returnUrl: window.location.origin + '/settings'
      });
      window.location.assign(data.url);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to open subscription management');
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!isPremium) {
      setError('Household sharing is a premium feature');
      return;
    }

    try {
      setError('');
      
      // Check if invited user exists
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', inviteEmail)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        setError('No user found with this email');
        return;
      }

      const invitedUser = { id: usersSnapshot.docs[0].id, ...usersSnapshot.docs[0].data() };

      // Check if user is already in a household
      const householdsQuery = query(
        collection(db, 'households'),
        where('members', 'array-contains', invitedUser.id)
      );
      const householdsSnapshot = await getDocs(householdsQuery);
      
      if (!householdsSnapshot.empty) {
        setError('User is already part of another household');
        return;
      }

      if (!household) {
        // Create new household
        const newHousehold = await addDoc(collection(db, 'households'), {
          owner: user.uid,
          members: [user.uid, invitedUser.id],
          createdAt: new Date().toISOString()
        });
        
        setHousehold({
          id: newHousehold.id,
          owner: user.uid,
          members: [user.uid, invitedUser.id],
          createdAt: new Date().toISOString()
        });
      } else {
        // Add member to existing household
        await updateDoc(doc(db, 'households', household.id), {
          members: arrayUnion(invitedUser.id)
        });

        setHousehold(prev => ({
          ...prev,
          members: [...prev.members, invitedUser.id]
        }));
      }

      setSharedUsers(prev => [...prev, invitedUser]);
      setInviteEmail('');
    } catch (error) {
      console.error('Error inviting user:', error);
      setError('Failed to invite user');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from your household?')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'households', household.id), {
        members: arrayRemove(userId)
      });

      setHousehold(prev => ({
        ...prev,
        members: prev.members.filter(id => id !== userId)
      }));

      setSharedUsers(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error removing user:', error);
      setError('Failed to remove user');
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // 1. Delete all user's rooms and their contents
      const roomsQuery = query(
        collection(db, 'rooms'),
        where('userId', '==', user.uid)
      );
      const roomsSnapshot = await getDocs(roomsQuery);
      
      for (const roomDoc of roomsSnapshot.docs) {
        // Delete all items in the room
        const itemsQuery = query(
          collection(db, 'items'),
          where('roomId', '==', roomDoc.id)
        );
        const itemsSnapshot = await getDocs(itemsQuery);
        
        for (const itemDoc of itemsSnapshot.docs) {
          // Delete item photos and receipts
          if (itemDoc.data().photoUrl) {
            const photoRef = ref(storage, itemDoc.data().photoUrl);
            await deleteObject(photoRef).catch(console.error);
          }
          if (itemDoc.data().receiptPhotoUrl) {
            const receiptRef = ref(storage, itemDoc.data().receiptPhotoUrl);
            await deleteObject(receiptRef).catch(console.error);
          }
          await deleteDoc(itemDoc.ref);
        }
        
        await deleteDoc(roomDoc.ref);
      }

      // 2. Delete user's household data
      if (household) {
        await deleteDoc(doc(db, 'households', household.id));
      }

      // 3. Delete user metrics
      await deleteDoc(doc(db, 'userMetrics', user.uid));

      // 4. Delete user document
      await deleteDoc(doc(db, 'users', user.uid));

      // 5. Delete Firebase Auth user
      await deleteUser(user);

      // 6. Logout
      await logout();

    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  if (loading) return <Container>Loading...</Container>;

  return (
    <Container>
      <h1>Settings</h1>

      <Section>
        <SectionHeader>
          <h2>Subscription</h2>
        </SectionHeader>
        
        {user?.isPremium ? (
          <>
            <p>You currently have an active premium subscription.</p>
            <Button
              variant="secondary"
              onClick={handleManageSubscription}
            >
              Manage Subscription
            </Button>
          </>
        ) : (
          <>
            <p>Upgrade to premium to unlock all features.</p>
            <Button
              variant="primary"
              onClick={() => window.location.href = '/subscription'}
            >
              Upgrade to Premium
            </Button>
          </>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <h2>Household Sharing</h2>
          {!isPremium && (
            <Button variant="secondary" disabled>
              Premium Feature
            </Button>
          )}
        </SectionHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <Form onSubmit={handleInviteUser}>
          <Input
            type="email"
            placeholder="Enter email to invite"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={!isPremium}
            required
          />
          <Button 
            type="submit" 
            disabled={!isPremium}
          >
            Invite User
          </Button>
        </Form>

        <UserList>
          {sharedUsers.map(sharedUser => (
            <UserCard key={sharedUser.id}>
              <UserInfo>
                <h3>{sharedUser.email}</h3>
                {household?.owner === user.uid && (
                  <p>Shared by you</p>
                )}
              </UserInfo>
              {household?.owner === user.uid && (
                <Button
                  variant="danger"
                  onClick={() => handleRemoveUser(sharedUser.id)}
                >
                  Remove
                </Button>
              )}
            </UserCard>
          ))}
        </UserList>
      </Section>

      <DangerZone>
        <SectionHeader>
          <h2>Danger Zone</h2>
        </SectionHeader>
        <p>Deleting your account will remove all your data and is irreversible.</p>
        <Button 
          variant="danger" 
          onClick={() => setIsDeleteModalOpen(true)}
        >
          Delete Account
        </Button>
      </DangerZone>

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        isLoading={isDeleting}
      />
    </Container>
  );
};

export default Settings; 